import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { FaEnvelope } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    setMessage("");

    try {
      const res = await fetch("/api/membre?action=requestReset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      setSending(false);

      if (!data.success) {
        if (data.code === "email_required") {
          setMessage(t("forgotPassword.errors.emailRequired"));
          return;
        }

        setMessage(t("forgotPassword.errors.server"));
        return;
      }

      setMessage(t("forgotPassword.success"));

      setTimeout(() => {
        window.location.href = `/membre/verify?email=${encodeURIComponent(
          email,
        )}`;
      }, 1200);
    } catch (err) {
      setMessage(t("forgotPassword.errors.server"));
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-800">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="
          w-full max-w-md bg-white/95 rounded-3xl shadow-2xl 
          px-7 py-8 sm:px-9 sm:py-9
        "
      >
        <div className="flex justify-center mb-4">
          <div className="bg-indigo-100 p-4 rounded-2xl shadow-md">
            <FaEnvelope size={26} className="text-indigo-600" />
          </div>
        </div>

        <h1 className="text-xl sm:text-2xl font-extrabold text-center text-slate-900">
          {t("forgotPassword.title")}
        </h1>

        <p className="text-sm text-slate-500 text-center mt-2 mb-6">
          {t("forgotPassword.description")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5" method="POST">
          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              {t("forgotPassword.email")}
            </label>
            <input
              ref={emailRef}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder={t("forgotPassword.emailPlaceholder")}
            />
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            disabled={sending}
            className={`
              w-full py-2.5 rounded-xl font-semibold text-white text-sm shadow-md transition 
              flex items-center justify-center
              ${
                sending
                  ? "bg-indigo-300 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }
            `}
          >
            {sending ? t("forgotPassword.sending") : t("forgotPassword.submit")}
          </motion.button>
        </form>

        {message && (
          <p className="text-center mt-4 text-sm text-indigo-700 font-medium">
            {message}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-1 text-sm">
          <button
            type="button"
            onClick={() => (window.location.href = "/membre/login")}
            className="w-full text-center text-slate-500 hover:text-slate-700 underline"
          >
            {t("forgotPassword.backLogin")}
          </button>

          <button
            type="button"
            onClick={() => (window.location.href = "/")}
            className="w-full text-center text-slate-500 hover:text-slate-700 underline"
          >
            {t("forgotPassword.backHome")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
